/**
 * SEO utility functions for managing meta tags
 */

interface SEOConfig {
  title?: string;
  description?: string;
  keywords?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogUrl?: string;
}

/**
 * Update document meta tags for SEO
 */
export const updateSEO = (config: SEOConfig) => {
  // Update title
  if (config.title) {
    document.title = config.title;
  }

  // Update or create meta description
  if (config.description) {
    updateMetaTag('name', 'description', config.description);
  }

  // Update or create meta keywords
  if (config.keywords) {
    updateMetaTag('name', 'keywords', config.keywords);
  }

  // Update Open Graph tags
  if (config.ogTitle) {
    updateMetaTag('property', 'og:title', config.ogTitle);
  }

  if (config.ogDescription) {
    updateMetaTag('property', 'og:description', config.ogDescription);
  }

  if (config.ogUrl) {
    updateMetaTag('property', 'og:url', config.ogUrl);
  }
};

/**
 * Helper function to update or create meta tags
 */
const updateMetaTag = (attribute: string, name: string, content: string) => {
  let element = document.querySelector(`meta[${attribute}="${name}"]`) as HTMLMetaElement;
  
  if (element) {
    element.content = content;
  } else {
    element = document.createElement('meta');
    element.setAttribute(attribute, name);
    element.content = content;
    document.head.appendChild(element);
  }
};

/**
 * Default SEO configurations for different pages
 */
export const SEO_CONFIGS = {
  default: {
    title: '中科志愿_2025高考志愿填报-新高考志愿填报与选科指南',
    description: '中科志愿_2025高考志愿填报系统，专业的新高考志愿填报与选科指南平台。提供高考志愿填报咨询、专业选择指导、院校信息查询、生涯规划服务。助力2025届高考生科学填报志愿，实现理想大学梦想。',
    keywords: '高考志愿填报,2025高考,新高考,志愿填报系统,选科指南,高考咨询,专业选择,院校查询,生涯规划,志愿填报指导,高考志愿,大学专业,录取分数线,高考改革,3+1+2模式,高中选科',
    ogTitle: '中科志愿_2025高考志愿填报-新高考志愿填报与选科指南',
    ogDescription: '专业的高考志愿填报平台，提供2025高考志愿填报咨询、选科指导、院校专业查询等服务，助力高考生科学填报志愿。',
    ogUrl: 'https://www.zhkzhy.com'
  },
  login: {
    title: '用户登录 - 中科志愿_2025高考志愿填报系统',
    description: '登录中科志愿高考志愿填报系统，开始您的2025高考志愿填报之旅。专业的志愿填报指导，助您选择理想大学和专业。',
    keywords: '高考志愿填报登录,志愿填报系统登录,中科志愿登录,高考咨询平台,2025高考',
    ogTitle: '用户登录 - 中科志愿高考志愿填报系统',
    ogDescription: '登录中科志愿高考志愿填报系统，获得专业的志愿填报指导服务。'
  },
  collegeAdmission: {
    title: '院校专业查询 - 中科志愿_2025高考志愿填报系统',
    description: '查询全国高校招生信息、专业录取分数线、院校排名等详细数据。为2025高考志愿填报提供权威参考，科学选择心仪院校和专业。',
    keywords: '院校查询,专业查询,录取分数线,高校排名,招生计划,志愿填报数据,大学信息,专业分数线,高考录取,院校对比',
    ogTitle: '院校专业查询 - 中科志愿高考志愿填报系统',
    ogDescription: '查询全国高校招生信息、专业录取分数线，为高考志愿填报提供权威数据参考。'
  },
  userAgreement: {
    title: '用户协议 - 中科志愿_2025高考志愿填报系统',
    description: '中科志愿用户服务协议，了解平台服务条款、用户权益、隐私保护等重要信息。使用前请仔细阅读相关条款。',
    keywords: '用户协议,服务条款,隐私政策,中科志愿协议,平台规则,用户权益',
    ogTitle: '用户协议 - 中科志愿高考志愿填报系统',
    ogDescription: '中科志愿用户服务协议，了解平台服务条款和用户权益保护。'
  },
  roleSelect: {
    title: '角色选择 - 中科志愿_2025高考志愿填报系统',
    description: '选择您的身份类型：个人规划师或机构入驻。加入中科志愿平台，为高考生提供专业的志愿填报指导服务，共同助力学生实现大学梦想。',
    keywords: '规划师入驻,机构入驻,志愿填报师,高考咨询师,教育机构,生涯规划师,志愿填报服务',
    ogTitle: '角色选择 - 中科志愿高考志愿填报系统',
    ogDescription: '选择个人规划师或机构入驻，加入中科志愿平台，为高考生提供专业志愿填报指导。'
  }
};

/**
 * Set SEO for specific page
 */
export const setSEO = (pageKey: keyof typeof SEO_CONFIGS) => {
  const config = SEO_CONFIGS[pageKey] || SEO_CONFIGS.default;
  updateSEO(config);
}; 